// Outgoing trucks analytics (admin-only) — Suresh 2026-09-30.
// Purpose: outsource-vs-buy analysis for hauling. Shows how many hauler
// trucks we actually use per day (avg/min/max), hauling cost per month,
// and the split by material. Data: /api/loads-analytics.
import { useState, useEffect } from 'react';
import { useApi } from '../api.js';

// Researched CAD defaults (2026-09-30 market check: new Ontario
// tri-axle ~$250-330K; driver ~$65-80K + burdens; insurance ~$7-10K;
// maintenance+tires ~$15-20K; fuel $30-50K busy truck). Midpoints
// below; every field editable and saved server-side once changed.
const OWNERSHIP_DEFAULTS = {
  purchasePrice: 290000,
  amortYears: 8,
  driver: 83000,
  insurance: 8500,
  maintenance: 17500,
  fuel: 40000,
  licensingMisc: 5000,
  loadsPerTruckDay: 4,   // how many loads one owned truck can run per day
  trucksNeeded: 0        // 0 = derive from avg loads/day / loadsPerTruckDay
};
import { BarChart, Bar, Cell, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { CostTrends, RateCharts } from './CostTrends.jsx';

const money = (n) => '$' + Math.round(n ?? 0).toLocaleString();
const num = (n) => (Number(n) || 0).toLocaleString();

// view comes from the ROUTE now (Suresh: separate nav tabs, not
// subtabs): /outgoing-analytics renders 'analysis',
// /buy-vs-outsource renders 'buy'.
export default function OutgoingAnalytics({ view = 'analysis' }) {
  const [months, setMonths] = useState(12);
  const { data, error, loading, refresh } = useApi('/api/loads-analytics?months=' + months);

  // Hauler rates + ownership model (admin inputs)
  const [haulers, setHaulers] = useState([]);
  const [own, setOwn] = useState(OWNERSHIP_DEFAULTS);
  const [rateBusy, setRateBusy] = useState(null);
  async function loadRates() {
    try {
      const r = await fetch('/api/hauler-rates', { credentials: 'include' });
      if (!r.ok) return;
      const j = await r.json();
      setHaulers(j.haulers || []);
      if (j.ownership) setOwn({ ...OWNERSHIP_DEFAULTS, ...j.ownership });
    } catch (_) {}
  }
  useEffect(() => { loadRates(); }, []);
  async function saveRate(h) {
    setRateBusy(h.hauler);
    try {
      const r = await fetch('/api/hauler-rates', {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ op: 'rate', hauler: h.hauler, ratePerLoad: h.ratePerLoad, notes: h.notes })
      });
      if (!r.ok) throw new Error(await r.text());
      refresh();   // estimates feed the analytics
    } catch (e) { alert('Rate save failed: ' + e.message); }
    finally { setRateBusy(null); }
  }
  async function saveOwnership(model) {
    try {
      const r = await fetch('/api/hauler-rates', {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ op: 'ownership', model })
      });
      if (!r.ok) throw new Error(await r.text());
    } catch (e) { alert('Model save failed: ' + e.message); }
  }

  if (loading) return <div className="loading">Crunching outgoing-load analytics…</div>;
  if (error)   return <div className="error">Error: {error}</div>;
  if (!data)   return null;

  const s = data.summary || {};
  const monthly = data.monthly || [];
  const daily = (data.daily || []).slice(-90);
  const byMaterial = data.byMaterial || [];

  const uncovered = (s.rowsMissingCost || 0) - (s.estimatedLoadsCovered || 0);
  const costCoverageNote = s.rowsMissingCost > 0
    ? (s.estimatedLoadsCovered > 0
        ? s.estimatedLoadsCovered + ' of ' + s.rowsMissingCost + ' cost-less loads estimated from hauler rates'
          + (uncovered > 0 ? ' — ' + uncovered + ' still uncovered (enter rates for their haulers below).' : '. ✓')
        : s.rowsMissingCost + ' load(s) have NO cost recorded — enter hauler rates below to estimate them.')
    : null;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <div>
          <h2 style={{ margin: 0 }}>🚛 {view === 'buy' ? 'Trucks — buy vs outsource' : 'Outgoing trucks — analysis'}</h2>
          <div className="small" style={{ color: '#666' }}>
            Last {s.windowMonths} months · {num(s.activeDays)} active hauling days · all per-day stats are LOADS per day
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <select className="refresh-btn" value={months} onChange={e => setMonths(parseInt(e.target.value, 10))}>
            <option value={6}>Last 6 months</option>
            <option value={12}>Last 12 months</option>
            <option value={24}>Last 24 months</option>
          </select>
          <button className="refresh-btn" onClick={refresh}>Refresh</button>
        </div>
      </div>

      {costCoverageNote && (
        <div style={{ background: '#fff4e5', border: '1px solid #f59e0b', borderRadius: 6, padding: '0.5rem 0.75rem', marginBottom: '0.75rem', fontSize: '0.9em', color: '#78350f' }}>
          ⚠ {costCoverageNote}
        </div>
      )}

      {view === 'analysis' && (<>
      <div className="stat-row" style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <div className="card" style={{ padding: '0.75rem 1rem', flex: '1 1 160px' }}>
          <div className="small" style={{ color: '#666' }}>Avg loads / active day</div>
          <div style={{ fontSize: '1.7em', fontWeight: 700 }}>{s.loadsPerDay?.avg ?? 0}</div>
          <div className="sub small">min {s.loadsPerDay?.min ?? 0} · max {s.loadsPerDay?.max ?? 0}</div>
        </div>
        <div className="card" style={{ padding: '0.75rem 1rem', flex: '1 1 160px' }}>
          <div className="small" style={{ color: '#666' }}>Total hauling cost ({s.windowMonths} mo{s.estimatedCost > 0 ? ', incl. estimates' : ''})</div>
          <div style={{ fontSize: '1.7em', fontWeight: 700 }}>{money(s.totalCostWithEstimates ?? s.totalCost)}</div>
          <div className="sub small">avg {money(s.avgMonthlyWithEstimates ?? s.avgCostPerMonth)}/month{s.estimatedCost > 0 ? ' · ' + money(s.estimatedCost) + ' estimated from hauler rates' : ''}</div>
        </div>
        <div className="card" style={{ padding: '0.75rem 1rem', flex: '1 1 160px' }}>
          <div className="small" style={{ color: '#666' }}>Total loads out</div>
          <div style={{ fontSize: '1.7em', fontWeight: 700 }}>{num(s.totalLoads)}</div>
          <div className="sub small">{s.activeDays ? Math.round((s.totalLoads / s.activeDays) * 10) / 10 : 0} loads / active day</div>
        </div>
      </div>

      <div className="panel-row">
        <div className="panel">
          <h3>Monthly hauling cost</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={monthly}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis tickFormatter={(v) => '$' + (v / 1000).toFixed(0) + 'K'} />
              <Tooltip formatter={(v) => money(v)} />
              <Bar dataKey="cost_with_est" name="Cost (incl. est)" fill="#0e7490" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="panel">
          <h3>Loads per day (last 90 days)</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={daily}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tickFormatter={(d) => String(d).slice(5, 10)} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="loads" name="Loads" stroke="#0a8b3a" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <RateCharts isAdmin={true} />
      <CostTrends costTrends={data.costTrends} months={months} />
      {data.inOutCorrelation && data.inOutCorrelation.weekly.length > 5 && (() => {
        const c = data.inOutCorrelation;
        const headline = c.bestR != null
          ? (c.bestLagWeeks === 0
              ? 'Outgoing tracks incoming in the SAME week (r = ' + c.bestR + ')'
              : 'Outgoing best follows incoming with a ~' + c.bestLagWeeks + '-week lag (r = ' + c.bestR + ')')
          : 'Not enough overlapping weeks to estimate correlation yet.';
        const strength = c.bestR == null ? '' : c.bestR >= 0.7 ? 'strong' : c.bestR >= 0.4 ? 'moderate' : 'weak';
        return (
          <div className="card" style={{ padding: '0.75rem 1rem', marginBottom: '1rem' }}>
            <h3 style={{ marginTop: 0 }}>🔁 Incoming vs outgoing loads — correlation & lag</h3>
            <div style={{ fontWeight: 700, color: '#0f766e', marginBottom: '0.2rem' }}>{headline}{strength ? ' — ' + strength + ' relationship' : ''}</div>
            <div className="small" style={{ color: '#666', marginBottom: '0.5rem' }}>
              Weekly totals. The lag chart shows how well outgoing correlates with incoming shifted back N weeks —
              the peak estimates how long material dwells in processing before it ships out.
            </div>
            <div className="panel-row">
              <div className="panel" style={{ flex: '2 1 480px' }}>
                <h3>Weekly loads in vs out</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={c.weekly}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="week" tickFormatter={(w) => String(w).slice(5)} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="incoming" name="Incoming (tips)" stroke="#b45309" strokeWidth={1.8} dot={false} />
                    <Line type="monotone" dataKey="outgoing" name="Outgoing loads" stroke="#0e7490" strokeWidth={1.8} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="panel" style={{ flex: '1 1 260px' }}>
                <h3>Correlation by lag (weeks)</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={c.byLag}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="lagWeeks" />
                    <YAxis domain={[-1, 1]} tickFormatter={(v) => v.toFixed(1)} />
                    <Tooltip formatter={(v) => v} />
                    <Bar dataKey="r" name="Pearson r">
                      {c.byLag.map((l, i) => (
                        <Cell key={i} fill={l.lagWeeks === c.bestLagWeeks ? '#0f766e' : '#94a3b8'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        );
      })()}
      </>)}

      {view === 'buy' && (<>
      <div className="card" style={{ padding: '0.75rem 1rem', marginBottom: '1rem', background: '#f0f9ff', border: '1px solid #bae6fd' }}>
        <div className="small" style={{ color: '#075985', fontWeight: 600 }}>Demand from the data (last {s.windowMonths} months)</div>
        <div className="small" style={{ color: '#0c4a6e' }}>
          Average day = <strong>{s.loadsPerDay?.avg ?? 0}</strong> loads (min {s.loadsPerDay?.min ?? 0}, peak {s.loadsPerDay?.max ?? 0}) over {num(s.activeDays)} active days ·
          outsourced spend ≈ <strong>{money(s.avgMonthlyWithEstimates ?? s.avgCostPerMonth)}</strong>/month incl. rate estimates.
          Size a fleet by dividing the average day by loads one owned truck can run (calculator below).
        </div>
      </div>
      {(() => {
        // ── Outsource vs buy calculator ─────────────────────────────
        const perTruckAnnual = (own.purchasePrice / Math.max(1, own.amortYears))
          + own.driver + own.insurance + own.maintenance + own.fuel + own.licensingMisc;
        const lptd = Math.max(1, own.loadsPerTruckDay || 4);
        const trucks = own.trucksNeeded > 0
          ? own.trucksNeeded
          : Math.max(1, Math.ceil((s.loadsPerDay?.avg || 1) / lptd));
        const ownAnnual = perTruckAnnual * trucks;
        const outsourceAnnual = (s.avgMonthlyWithEstimates || s.avgCostPerMonth || 0) * 12;
        const delta = outsourceAnnual - ownAnnual;
        const field = (label, key, step) => (
          <label className="small" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ color: '#666' }}>{label}</span>
            <input type="number" step={step || 500} value={own[key]}
                   onChange={e => setOwn(o => ({ ...o, [key]: Number(e.target.value) || 0 }))}
                   onBlur={() => saveOwnership(own)}
                   style={{ width: 110, padding: '0.2rem 0.3rem' }} />
          </label>
        );
        return (
          <div className="card" style={{ padding: '0.75rem 1rem', marginBottom: '1rem', border: '1px solid #bae6fd', background: '#f0f9ff' }}>
            <h3 style={{ marginTop: 0 }}>⚖️ Outsource vs buy — plug in your numbers</h3>
            <div className="small" style={{ color: '#666', marginBottom: '0.5rem' }}>
              Prefilled with Sept 2026 Ontario market figures (new tri-axle, CAD) — every field editable; edits save automatically.
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
              {field('Truck price ($)', 'purchasePrice', 5000)}
              {field('Amortize (yrs)', 'amortYears', 1)}
              {field('Driver ($/yr)', 'driver')}
              {field('Insurance ($/yr)', 'insurance')}
              {field('Maint.+tires ($/yr)', 'maintenance')}
              {field('Fuel ($/yr)', 'fuel')}
              {field('Licences/misc ($/yr)', 'licensingMisc')}
              {field('Loads/truck/day', 'loadsPerTruckDay', 1)}
              {field('Trucks (0=auto)', 'trucksNeeded', 1)}
            </div>
            <table className="fs-table tidy" style={{ fontSize: '0.95em', maxWidth: 720 }}>
              <tbody>
                <tr><td>Cost per owned truck / year</td><td className="num" style={{ fontWeight: 600 }}>{money(perTruckAnnual)}</td></tr>
                <tr><td>Trucks modelled {own.trucksNeeded > 0 ? '(manual)' : '(avg ' + (s.loadsPerDay?.avg ?? 0) + ' loads/day ÷ ' + lptd + ' loads/truck)'}</td><td className="num" style={{ fontWeight: 600 }}>{trucks}</td></tr>
                <tr><td>OWN: fleet cost / year</td><td className="num" style={{ fontWeight: 700 }}>{money(ownAnnual)}</td></tr>
                <tr><td>OUTSOURCE: current spend / year {s.estimatedCost > 0 ? '(incl. estimates)' : ''}</td><td className="num" style={{ fontWeight: 700 }}>{money(outsourceAnnual)}</td></tr>
                <tr style={{ background: delta > 0 ? '#dcfce7' : '#fee2e2' }}>
                  <td style={{ fontWeight: 700 }}>{delta > 0 ? 'Owning saves' : 'Outsourcing saves'} (per year)</td>
                  <td className="num" style={{ fontWeight: 700, color: delta > 0 ? '#15803d' : '#b91c1c' }}>{money(Math.abs(delta))}</td>
                </tr>
              </tbody>
            </table>
            {byMaterial.length > 0 && (() => {
              // Per-material break-even: sand vs clay etc. ship at
              // different rates, so allocate the owned-fleet cost by
              // load share and compare per material.
              const totLoads = byMaterial.reduce((t, m) => t + m.loads, 0) || 1;
              const annualize = (v) => (Number(v) || 0) * 12 / (s.windowMonths || 12);
              return (
                <div style={{ marginTop: '0.6rem' }}>
                  <div style={{ fontWeight: 600, marginBottom: '0.2rem' }}>By material (sand vs clay etc.)</div>
                  <table className="fs-table tidy" style={{ fontSize: '0.9em' }}>
                    <thead><tr>
                      <th>Material</th>
                      <th className="num">Load share</th>
                      <th className="num">$/load (incl. est)</th>
                      <th className="num">Outsource $/yr</th>
                      <th className="num">Own (allocated) $/yr</th>
                      <th className="num">Owning saves</th>
                    </tr></thead>
                    <tbody>
                      {byMaterial.map(m => {
                        const share = m.loads / totLoads;
                        const matCost = Number(m.cost_with_est ?? m.cost) || 0;
                        const outYr = annualize(matCost);
                        const ownYr = ownAnnual * share;
                        const d = outYr - ownYr;
                        // No cost data (recorded or estimated) for this
                        // material -> a $0-vs-own comparison is nonsense;
                        // say so instead of claiming owning "loses".
                        const noCost = matCost <= 0;
                        return (
                          <tr key={m.material}>
                            <td style={{ fontWeight: 600 }}>{m.material}</td>
                            <td className="num">{Math.round(share * 100)}%</td>
                            <td className="num">{noCost ? '—' : money(matCost / m.loads)}</td>
                            <td className="num">{noCost ? 'no cost data' : money(outYr)}</td>
                            <td className="num">{money(ownYr)}</td>
                            <td className="num" style={{ fontWeight: 600, color: noCost ? '#64748b' : d > 0 ? '#15803d' : '#b91c1c' }}>
                              {noCost ? 'enter hauler rates' : (d > 0 ? money(d) : '−' + money(Math.abs(d)))}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="note">Own $/yr allocated by load share of the modelled fleet. A material that's cheap to outsource ($/load below your per-load ownership cost) is a keep-outsourcing candidate even if the fleet overall pencils out.</div>
                </div>
              );
            })()}
            <div className="note" style={{ marginTop: '0.4rem' }}>
              Peak day = {s.loadsPerDay?.max ?? '?'} loads vs {s.loadsPerDay?.avg ?? '?'} average — a mixed model
              (own the base, outsource the spikes) usually beats both extremes. Idle owned trucks still cost driver + financing.
            </div>
          </div>
        );
      })()}
      </>)}

      {view === 'analysis' && (<>
      <div className="card" style={{ padding: '0.75rem 1rem', marginBottom: '1rem' }}>
        <h3 style={{ marginTop: 0 }}>🚚 Hauler rates — enter cost per load</h3>
        <div className="small" style={{ color: '#666', marginBottom: '0.5rem' }}>
          Every trucking company seen in the outgoing loads (last 24 months). "No recorded cost" = the import had no
          cost on those loads; that count never goes down. Saving a rate doesn't rewrite them — it ESTIMATES them
          (✓ est. = covered, feeds the † totals above; ⚠ = enter a rate).
        </div>
        <table className="fs-table tidy" style={{ fontSize: '0.9em' }}>
          <thead><tr>
            <th>Hauler</th>
            <th className="num">Loads (24 mo)</th>
            <th className="num" title="Loads imported without a cost. A saved rate ESTIMATES these — it doesn't rewrite the rows.">No recorded cost</th>
            <th className="num">Avg recorded $/load</th>
            <th className="num">Your rate $/load</th>
            <th>Notes</th>
            <th></th>
          </tr></thead>
          <tbody>
            {haulers.map((h, i) => (
              <tr key={h.hauler}>
                <td style={{ fontWeight: 600 }}>{h.hauler}</td>
                <td className="num">{num(h.loads)}</td>
                <td className="num" style={{ color: h.loadsMissingCost > 0 ? (h.ratePerLoad != null ? '#15803d' : '#c05100') : '#94a3b8' }}
                    title={h.loadsMissingCost > 0 ? (h.ratePerLoad != null ? 'Covered: estimated at your rate' : 'Not covered: enter a rate to estimate these loads') : ''}>
                  {h.loadsMissingCost ? (h.ratePerLoad != null ? h.loadsMissingCost + ' ✓ est.' : h.loadsMissingCost + ' ⚠') : '—'}
                </td>
                <td className="num">{h.avgRecordedCost != null ? money(h.avgRecordedCost) : '—'}</td>
                <td className="num">
                  <input type="number" step="5" min="0" value={h.ratePerLoad ?? ''}
                         placeholder="—"
                         onChange={e => setHaulers(list => list.map((x, j) => j === i ? { ...x, ratePerLoad: e.target.value === '' ? null : Number(e.target.value) } : x))}
                         style={{ width: 90, padding: '0.2rem 0.3rem', textAlign: 'right' }} />
                </td>
                <td>
                  <input value={h.notes || ''}
                         onChange={e => setHaulers(list => list.map((x, j) => j === i ? { ...x, notes: e.target.value } : x))}
                         style={{ width: '100%', padding: '0.2rem 0.3rem' }} />
                </td>
                <td>
                  <button className="refresh-btn small" disabled={rateBusy === h.hauler} onClick={() => saveRate(h)}>
                    {rateBusy === h.hauler ? '…' : 'Save'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card" style={{ padding: '0.75rem 1rem', marginBottom: '1rem' }}>
        <h3 style={{ marginTop: 0 }}>By material (dump-site waste type) — last {s.windowMonths} months</h3>
        <div className="small" style={{ color: '#666', marginBottom: '0.4rem' }}>
          Same classification as the Overview tab: each load's destination mapped to the vendor's waste type.
          'Unclassified' = destination has no waste type set — fix it on the Vendor Spend tab.
        </div>
        <table className="fs-table tidy" style={{ fontSize: '0.9em' }}>
          <thead><tr>
            <th>Material</th>
            <th className="num">Loads</th>
            <th className="num">Quantity</th>
            <th>Units</th>
            <th className="num">Dump sites</th>
            <th className="num">Active days</th>
            <th className="num">Total cost</th>
            <th className="num">Avg cost/load</th>
          </tr></thead>
          <tbody>
            {byMaterial.map(m => (
              <tr key={m.material}>
                <td style={{ fontWeight: 600 }}>{m.material}</td>
                <td className="num">{num(m.loads)}</td>
                <td className="num">{num(m.quantity)}</td>
                <td className="small">{m.units || '—'}</td>
                <td className="num">{num(m.destinations)}</td>
                <td className="num">{num(m.active_days)}</td>
                <td className="num" style={{ fontWeight: 600 }}>{money(m.cost_with_est ?? m.cost)}{m.est_cost > 0 ? ' †' : (m.rows_missing_cost > 0 ? ' *' : '')}</td>
                <td className="num">{m.loads > 0 ? money((m.cost_with_est ?? m.cost) / m.loads) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="note">* some loads for this material have no cost recorded — total understates spend. Quantities with mixed units don't sum meaningfully; check the Units column.</div>
      </div>

      <div className="card" style={{ padding: '0.75rem 1rem' }}>
        <h3 style={{ marginTop: 0 }}>Monthly detail</h3>
        <table className="fs-table tidy" style={{ fontSize: '0.9em' }}>
          <thead><tr>
            <th>Month</th>
            <th className="num">Loads</th>
            <th className="num">Active days</th>
            <th className="num">Loads avg/day</th>
            <th className="num">min</th>
            <th className="num">max</th>
            <th className="num">Cost</th>
          </tr></thead>
          <tbody>
            {monthly.map(m => (
              <tr key={m.month}>
                <td>{m.month}</td>
                <td className="num">{num(m.loads)}</td>
                <td className="num">{num(m.active_days)}</td>
                <td className="num" style={{ fontWeight: 600 }}>{m.loads_avg}</td>
                <td className="num">{m.loads_min}</td>
                <td className="num">{m.loads_max}</td>
                <td className="num" style={{ fontWeight: 600 }}>{money(m.cost_with_est ?? m.cost)}{m.est_cost > 0 ? ' †' : (m.rows_missing_cost > 0 ? ' *' : '')}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="note">† includes estimates from hauler rates · * has loads with no cost and no hauler rate</div>
      </div>
      </>)}
    </div>
  );
}
